import { CommonModule, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Input, OnChanges } from '@angular/core';
import { SelectableConnection } from '../../models/SelectableConnection';
import { ConnectionEvents } from '../../models/connection-events';

type Position = { x: number; y: number };

/**
 * Composant utilisé pour le rendu d'un évènement (il s'agit d'une connection dans le framework RETE).
 */
@Component({
    selector: 'connection',
    imports: [CommonModule, NgClass],
    templateUrl: './custom-connection.component.html',
    styleUrl: './custom-connection.component.scss'
})
export class CustomConnectionComponent implements OnChanges {
    /**
     * Données de la connexion entre 2 noeuds.
     */
    _data!: SelectableConnection;

    /**
     * Spécifie les données de la connexion entre 2 noeuds.
     */
    @Input() set data(value: SelectableConnection) {
        this._data = value;

        if (this._data.selected === true) {
            (this.host.nativeElement as HTMLElement).querySelector('svg').focus();
        }
    }

    /**
     * Position du point de départ de la connexion entre 2 noeuds.
     */
    private _start: Position;

    /**
     * Spécifie la position du point de départ de la connexion entre 2 noeuds..
     */
    @Input() set start(value: Position) {
        this._start = value;
        this.buildPath();
    }

    /**
     * Position du point de d'arrivée de la connexion entre 2 noeuds.
     */
    private _end: Position;

    /**
     * Spécifie la position du point d'arrivée de la connexion entre 2 noeuds..
     */
    @Input() set end(value: Position) {
        this._end = value;
        this.buildPath();
    }

    /**
     * Description de la forme courbe utilisée pour représenter graphiquement la connexion.
     */
    public connectionPath: string;
    /**
     * Valeur indiquant l'état de selection précedant de la connexion (true : selectionné, false : deselectionné).
     */
    previousSelectedState = undefined;

    /**
     * Constructeur.
     * @param cdr : Reference au detecteur de changement Angular.
     */
    constructor(
        private cdr: ChangeDetectorRef,
        private host: ElementRef
    ) {}

    /**
     * Appellé lorsqu'un changement est detecté sur le composant.
     */
    ngOnChanges(): void {
        console.log('CustomConnectionComponent ngOnChanges');
        this.cdr.detectChanges();
        this.emitSelectionChange();
    }

    ngAfterViewChecked() {
        if (this._data.selected === true) {
            // (this.host.nativeElement as HTMLElement).querySelector('svg').focus();
        }
    }

    /**
     * Emet une valeur indiquant que l'état de selection de la connexion a changé.
     */
    private emitSelectionChange() {
        // Si une fonction de callback est spécifié, execute la fonction
        if (this.data.callBack) {
            if (this.previousSelectedState !== this.data.selected) {
                if (this.data.selected === true) {
                    this.data.callBack(this.data.id, ConnectionEvents.Selected);
                    (this.host.nativeElement as HTMLElement).querySelector('svg').focus();
                } else if (this.data.selected === false) {
                    this.data.callBack(this.data.id, ConnectionEvents.Unselected);
                }
                this.previousSelectedState = this.data.selected;
            }
        }
    }

    /**
     * Construit la forme courbe utilisée pour représenter graphiquement la connexion.
     */
    private buildPath() {
        if (this._start && this._end) {
            if (this._data.source === this._data.target) {
                // Cas où le noeud source et le noeud destination sont identiques
                const curvature = 0.8;

                this.connectionPath = this.generateSimplePath(this._start, this._end);
                //console.log(`buildPath : ${this.path2}`);
            } else {
                // Cas général
                const curvature = 0.8;
                this.connectionPath = this.generateWavePath(this._start, this._end);
            }
        }
    }

    /**
     * Génère une courbe en forme de vague pour représenter graphiquement la connexion.
     * @param start : Point de départ de la courbe.
     * @param end : Point d'arrivée de la courbe.
     * @returns : La description de la forme courbe utilisée pour représenter graphiquement la connexion.
     */
    private generateWavePath(start: Position, end: Position) {
        const curvature = 0.4;

        // Calculer les points de contrôle
        const cp1x = start.x + (end.x - start.x) * curvature;
        const cp1y = start.y;
        const cp2x = end.x - (end.x - start.x) * curvature;
        const cp2y = end.y;

        // Générer la commande de chemin SVG
        const d = `M ${start.x} ${start.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${end.x} ${end.y}`;

        return d;
    }

    /**
     * Génère une courbe 'simple' pour représenter graphiquement une connexion entre une socket de sortie et une socket d'entrée
     * d'un même noeud.
     * @param start : Point de départ de la courbe.
     * @param end : Point d'arrivée de la courbe.
     * @returns : La description de la forme courbe utilisée pour représenter graphiquement la connexion.
     */
    private generateSimplePath(start: Position, end: Position) {
        const radius = 50;

        // Définir les points de contrôle de la courbe Bézier
        const controlPoint1 = { x: start.x + radius, y: start.y + 2 * radius };
        const controlPoint2 = { x: end.x - radius, y: end.y + 2 * radius };

        // Créer le chemin SVG
        const svgPath = `M ${start.x} ${start.y} 
                   C ${controlPoint1.x} ${controlPoint1.y} 
                     ${controlPoint2.x} ${controlPoint2.y} 
                     ${end.x} ${end.y}`;

        return svgPath;
    }
}
